/**
 * Basic-mode bucket-span pricing (ADR 0059; the portal repo's
 * docs/ROOM_SKETCH_UI_PLAN.md + docs/bucket-span-preview.md).
 *
 * A STAGED bucket card ("6 × hanging section") prices as a SPAN across the
 * cv_section_catalog rows the bucket covers, narrowed by the space's
 * designer-picked bounds (lower height+depth, upper height+depth):
 *
 *   low  = the cheapest qualifying row × qty × range-low%
 *   high = the most expensive qualifying row × qty × range-high%
 *
 * The two ends are deliberately DIFFERENT concrete interpretations of the
 * same count (owner example: low six 84×12 open sections, high six 95×16
 * with doors) — door-config rows (DHDr, FS-2dr, ShDr) sit inside the
 * qualifying set wherever the bucket's config list spans them, so "the high
 * has doors" without a separate doors input.
 *
 * Conventions shared with space-pricing.js (ADR 0058):
 *   - the material-level factor scales materialPrice ONLY;
 *   - the garage platform charge ($/section, unscaled) is added per unit
 *     before the spread when the space stands on a platform;
 *   - the 100/125 spread rides last; display rounds $10 per line and $100
 *     for the space total, with *Exact twins carrying the unrounded sums.
 *
 * The pencil-sharpening invariant lives in priceBasicSpace(): the space
 * range = PLACED sections (priceSpace, the 4e pipeline) + still-STAGED
 * cards, so every conversion replaces a catalog-wide span with a real
 * price and can only narrow the range.
 *
 * Pure over the model + resolved rules — no DOM, no state; every price is
 * catalog data (no-guessing: an empty qualifying set is a visible
 * needs-pricing line, never a silent zero).
 */
import { levelFactor, priceSpace, roundToStep } from './space-pricing.js';

const cents = (v) => Math.round(v * 100) / 100;

/** The bucket definition for a key, or null. */
export function bucketByKey(rules, key) {
  return (Array.isArray(rules.basicBuckets) ? rules.basicBuckets : [])
    .find((b) => b?.key === key) ?? null;
}

/** Buckets the availability matrix offers for a space type (unknown or null
 *  space type ⇒ every bucket — availability never hides existing cards). */
export function availableBuckets(rules, spaceType) {
  const buckets = Array.isArray(rules.basicBuckets) ? rules.basicBuckets : [];
  const matrix = rules.basicBucketAvailability;
  if (!spaceType || !matrix || typeof matrix !== 'object') return buckets;
  // A space type the rule does not know cannot mean "hide everything" — it
  // filters nothing, like no type at all (rules-blind stored data tolerance).
  if (!(rules.basicSpaceTypes ?? []).some((t) => t?.key === spaceType)) return buckets;
  return buckets.filter((b) => {
    const types = matrix[b.key];
    return !Array.isArray(types) || types.includes(spaceType);
  });
}

/**
 * The designer bounds a 'space'-mode bucket is narrowed by: the sketch's own
 * `basic.bounds` when set, else the rule default. Shape:
 * { lowHeightIn, lowDepthIn, highHeightIn, highDepthIn }.
 */
export function resolvedBasicBounds(state, rules) {
  const b = state?.basic?.bounds;
  const d = rules.basicDefaultBounds ?? {};
  const n = (v, fb) => (Number.isFinite(v) && v > 0 ? v : fb);
  return {
    lowHeightIn: n(b?.lowHeightIn, n(d.lowHeightIn, 84)),
    lowDepthIn: n(b?.lowDepthIn, n(d.lowDepthIn, 12)),
    highHeightIn: n(b?.highHeightIn, n(d.highHeightIn, 95)),
    highDepthIn: n(b?.highDepthIn, n(d.highDepthIn, 16)),
  };
}

/**
 * The catalog rows a bucket spans at the given designer bounds.
 *   - boundsMode 'all': every row of the bucket's classes+configs (span doc:
 *     stacked/wall-bed/L-shelves are unbounded).
 *   - boundsMode 'own': the bucket's own fixed data bounds (base/upper live
 *     on their own height ladder).
 *   - boundsMode 'space' (default): the designer bounds, with the bucket's
 *     depthFloorIn raising the lower depth (drawer boxes need 16").
 */
export function qualifyingRows(rules, bucket, bounds) {
  const sections = rules.cvSectionCatalog?.sections ?? [];
  const classes = new Set(Array.isArray(bucket?.classes) ? bucket.classes : []);
  const configs = new Set(Array.isArray(bucket?.configs) ? bucket.configs : []);
  const pool = sections.filter((r) => classes.has(r.class) && configs.has(r.config));
  if (bucket?.boundsMode === 'all') return pool;
  const b = bucket?.boundsMode === 'own' ? (bucket.bounds ?? {}) : (bounds ?? {});
  const lowH = Number.isFinite(b.lowHeightIn) ? b.lowHeightIn : -Infinity;
  const highH = Number.isFinite(b.highHeightIn) ? b.highHeightIn : Infinity;
  let lowD = Number.isFinite(b.lowDepthIn) ? b.lowDepthIn : -Infinity;
  const highD = Number.isFinite(b.highDepthIn) ? b.highDepthIn : Infinity;
  if (bucket?.depthFloorIn > 0 && bucket.boundsMode !== 'own') lowD = Math.max(lowD, bucket.depthFloorIn);
  return pool.filter((r) => r.heightIn >= lowH - 1e-6 && r.heightIn <= highH + 1e-6
    && r.depthIn >= lowD - 1e-6 && r.depthIn <= highD + 1e-6);
}

/**
 * One UNIT's leveled low/high figures for a bucket at the given bounds.
 * A row's leveled price = nonMaterialPrice + materialPrice × the level's
 * factor (the multiplier scales material only — the wall-bed split
 * mechanism), plus the flat platform charge when one applies. The spread is
 * NOT applied here — the caller does, per line.
 *
 * @returns {{ok:true, lowUnit:number, highUnit:number, lowSource:string,
 *            highSource:string, rowCount:number} | {ok:false, reason:string}}
 */
export function bucketSpanUnit(rules, bucket, bounds, { level = 1, platformChargePerSection = 0 } = {}) {
  if (!bucket) return { ok: false, reason: 'unknown bucket — needs a definition in the basic_buckets rule' };
  const rows = qualifyingRows(rules, bucket, bounds);
  if (!rows.length) {
    return { ok: false, reason: `no ${bucket.label ?? bucket.key} rows qualify inside the current bounds` };
  }
  const factor = levelFactor(rules, level);
  const charge = platformChargePerSection > 0 ? platformChargePerSection : 0;
  const leveled = (r) => cents(r.nonMaterialPrice + r.materialPrice * factor + charge);
  let lowRow = rows[0];
  let highRow = rows[0];
  for (const r of rows) {
    if (leveled(r) < leveled(lowRow)) lowRow = r;
    if (leveled(r) > leveled(highRow)) highRow = r;
  }
  return {
    ok: true,
    lowUnit: leveled(lowRow),
    highUnit: leveled(highRow),
    lowSource: lowRow.source,
    highSource: highRow.source,
    rowCount: rows.length,
  };
}

/**
 * The DRAWER ADDER for one staged card (owner, 2026-08-10).
 *
 * `card.drawers` is a count PER UNIT — the owner's phrasing is "4 drawers added
 * to the shelving unit" — so a card of qty 2 carrying 4 drawers is 8 drawers.
 * Each drawer adds a flat `basic_drawer_adder_price_low`–`_high` band, which
 * is why Basic never needs a 1/2/3/4/5/6-drawer catalog row.
 *
 * ⚠ The 100/125 spread is deliberately NOT applied to these figures — the
 * spread the owner quoted is already the distance between $225 and $325. The
 * caller adds `lowExact`/`highExact` to the span AFTER the span has taken its
 * percentages, so the adder rides at face value.
 *
 * @returns {{drawers:number, perUnit:number, unitLow:number, unitHigh:number,
 *            lowExact:number, highExact:number} | null} null when the card
 *          carries no drawers (the overwhelmingly common case — no adder line,
 *          no note, nothing added).
 */
export function drawerAdderFor(rules, card) {
  const perUnit = Number.isInteger(card?.drawers) && card.drawers > 0 ? card.drawers : 0;
  const qty = Number.isInteger(card?.qty) && card.qty > 0 ? card.qty : 0;
  const drawers = perUnit * qty;
  if (drawers <= 0) return null;
  const unitLow = Number.isFinite(rules.basicDrawerAdderPriceLow) && rules.basicDrawerAdderPriceLow >= 0
    ? rules.basicDrawerAdderPriceLow : 0;
  const unitHigh = Number.isFinite(rules.basicDrawerAdderPriceHigh) && rules.basicDrawerAdderPriceHigh >= 0
    ? rules.basicDrawerAdderPriceHigh : 0;
  return {
    drawers,
    perUnit,
    unitLow,
    unitHigh,
    lowExact: cents(unitLow * drawers),
    highExact: cents(unitHigh * drawers),
  };
}

/**
 * Prices every STAGED card in the tray. Wall assignment is spatial context
 * only and never changes a card's span (UI plan: assignment is not
 * placement). Returns
 * { lines, lowExact, highExact, low, high, needsPricing } where each line is
 * { cardId, bucketKey, label, qty, wallId, basis: 'span'|'needs-pricing',
 *   lowExact, highExact, low, high, anchors, notes, adder }.
 *
 * `adder` is the drawer adder (or null). On a SPAN line it is already inside
 * lowExact/highExact; on a NEEDS-PRICING line the money is null, so the adder
 * is reported in the notes rather than billed on its own — a card whose section
 * cannot be priced does not become priceable because drawers were added to it.
 */
export function priceStagedCards(model, rules) {
  const cards = Array.isArray(model.state.basic?.cards) ? model.state.basic.cards : [];
  const bounds = resolvedBasicBounds(model.state, rules);
  const level = Number.isInteger(model.state.pricing?.level) && model.state.pricing.level >= 1
    ? model.state.pricing.level : 1;
  const lowPct = Number.isFinite(rules.pricingRangeLowPercent) ? rules.pricingRangeLowPercent : 100;
  const highPct = Number.isFinite(rules.pricingRangeHighPercent) ? rules.pricingRangeHighPercent : 125;
  const platformChargePerSection = model.state.platformHeightUnits > 0
    && Number.isFinite(rules.garagePlatformChargePerSection)
    ? rules.garagePlatformChargePerSection : 0;

  const lines = [];
  for (const card of cards) {
    if (!(card?.qty > 0)) continue;
    const bucket = bucketByKey(rules, card.bucketKey);
    const adder = drawerAdderFor(rules, card);
    const label = `${card.qty} × ${bucket?.label ?? card.bucketKey}`
      + (adder ? ` · ${adder.perUnit} drawer${adder.perUnit > 1 ? 's' : ''}/unit` : '');
    const span = bucketSpanUnit(rules, bucket, bounds, { level, platformChargePerSection });
    if (!span.ok) {
      lines.push({
        cardId: card.id, bucketKey: card.bucketKey, label, qty: card.qty, wallId: card.wallId ?? null,
        basis: 'needs-pricing', lowExact: null, highExact: null, low: null, high: null,
        anchors: [], adder,
        notes: [
          span.reason,
          ...(adder ? [`${adder.drawers} drawers at $${adder.unitLow}–$${adder.unitHigh} each are NOT included `
            + 'while the section itself needs pricing'] : []),
        ],
      });
      continue;
    }
    // The span takes the 100/125 spread; the adder does NOT (its own low/high
    // ARE the spread — see drawerAdderFor). Summed after, never multiplied in.
    const lowExact = cents(span.lowUnit * card.qty * (lowPct / 100) + (adder?.lowExact ?? 0));
    const highExact = cents(span.highUnit * card.qty * (highPct / 100) + (adder?.highExact ?? 0));
    lines.push({
      cardId: card.id,
      bucketKey: card.bucketKey,
      label,
      qty: card.qty,
      wallId: card.wallId ?? null,
      basis: 'span',
      lowExact,
      highExact,
      low: roundToStep(lowExact, 10),
      high: roundToStep(highExact, 10),
      anchors: [span.lowSource, span.highSource],
      adder,
      notes: [
        `span across ${span.rowCount} catalog row${span.rowCount > 1 ? 's' : ''}: `
          + `low ${span.lowSource} ($${span.lowUnit}/unit), high ${span.highSource} ($${span.highUnit}/unit)`,
        ...(platformChargePerSection > 0 ? [`includes $${platformChargePerSection}/section platform charge`] : []),
        ...(adder ? [`drawer adder: ${adder.perUnit}/unit × ${card.qty} = ${adder.drawers} drawers at `
          + `$${adder.unitLow}–$${adder.unitHigh} each = $${adder.lowExact}–$${adder.highExact} `
          + '(flat — the price-range % is not applied to the adder)'] : []),
      ],
    });
  }
  const priced = lines.filter((l) => l.lowExact !== null);
  const lowExact = cents(priced.reduce((s, l) => s + l.lowExact, 0));
  const highExact = cents(priced.reduce((s, l) => s + l.highExact, 0));
  return {
    lines,
    lowExact,
    highExact,
    low: roundToStep(lowExact, 10),
    high: roundToStep(highExact, 10),
    needsPricing: lines.filter((l) => l.basis === 'needs-pricing').length,
  };
}

/**
 * The whole space, both lenses combined: PLACED sections through the ADR
 * 0058 pipeline + still-STAGED cards as spans. The combined range is what
 * the Pricing panel shows and stamps — the pencil-sharpening invariant made
 * arithmetic: placing a unit moves it from a span term to an exact term.
 */
export function priceBasicSpace(model, rules) {
  const placed = priceSpace(model, rules, model.state.pricing);
  const staged = priceStagedCards(model, rules);
  const lowExact = cents(placed.space.lowExact + staged.lowExact);
  const highExact = cents(placed.space.highExact + staged.highExact);
  return {
    placed,
    staged,
    space: {
      lowExact,
      highExact,
      low: roundToStep(lowExact, 100),
      high: roundToStep(highExact, 100),
      needsPricing: placed.space.needsPricing + staged.needsPricing,
    },
  };
}

/**
 * A Quick Ballpark quick-mode estimate's core-section quantities → bucket
 * card quantities, via the quick_core_section_bucket_map rule (UI plan
 * §Adoption path — the import UI arrives with the transport work; this is
 * the pure half). Quantities on ids the map does not cover come back in
 * `unmapped`, visible, never guessed into a bucket.
 *
 * @param {Record<string, number>} coreSections quick-mode id → quantity
 * @returns {{cards: Array<{bucketKey:string, qty:number}>,
 *            unmapped: Array<{id:string, qty:number}>}}
 */
export function quickEstimateToBuckets(coreSections, rules) {
  const map = rules.quickCoreSectionBucketMap ?? {};
  const cards = new Map();
  const unmapped = [];
  for (const [id, raw] of Object.entries(coreSections && typeof coreSections === 'object' ? coreSections : {})) {
    const qty = Number.isFinite(raw) ? Math.max(0, Math.round(raw)) : 0;
    if (qty <= 0) continue;
    const bucketKey = typeof map[id] === 'string' ? map[id] : null;
    if (bucketKey && bucketByKey(rules, bucketKey)) {
      cards.set(bucketKey, (cards.get(bucketKey) ?? 0) + qty);
    } else {
      unmapped.push({ id, qty });
    }
  }
  return {
    cards: [...cards.entries()].map(([bucketKey, qty]) => ({ bucketKey, qty })),
    unmapped,
  };
}
